import { Component } from '@theme/component';

/**
 * @typedef {Object} TabsRefs
 * @property {HTMLElement} tablist
 * @property {HTMLButtonElement | HTMLButtonElement[]} tabButtons
 * @property {HTMLElement | HTMLElement[]} tabPanels
 */

class TabsComponent extends Component {
  requiredRefs = ['tablist', 'tabButtons', 'tabPanels'];

  #controller = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    if (this.#controller.signal.aborted) {
      this.#controller = new AbortController();
    }

    const { signal } = this.#controller;

    this.addEventListener('click', this.#handleClick, { signal });
    this.addEventListener('keydown', this.#handleKeydown, { signal });
    document.addEventListener('shopify:block:select', this.#handleBlockSelect, { signal });

    this.#syncSelectedTab();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#syncSelectedTab();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * @returns {HTMLButtonElement[]}
   */
  get #tabButtons() {
    const { tabButtons } = this.refs;

    if (Array.isArray(tabButtons)) return tabButtons;
    if (tabButtons instanceof HTMLButtonElement) return [tabButtons];

    return [];
  }

  /**
   * @returns {HTMLElement[]}
   */
  get #tabPanels() {
    const { tabPanels } = this.refs;

    if (Array.isArray(tabPanels)) return tabPanels;
    if (tabPanels instanceof HTMLElement) return [tabPanels];

    return [];
  }

  /**
   * @param {Event} event
   */
  #handleClick = (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-tab-trigger]') : null;

    if (!(button instanceof HTMLButtonElement) || !this.contains(button)) return;

    this.#selectTab(button);
  };

  /**
   * @param {KeyboardEvent} event
   */
  #handleKeydown = (event) => {
    const currentButton = event.target instanceof Element ? event.target.closest('[data-tab-trigger]') : null;

    if (!(currentButton instanceof HTMLButtonElement) || !this.contains(currentButton)) return;

    const buttons = this.#tabButtons;
    const currentIndex = buttons.indexOf(currentButton);

    if (currentIndex === -1) return;

    let nextIndex = currentIndex;

    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        nextIndex = (currentIndex + 1) % buttons.length;
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        nextIndex = (currentIndex - 1 + buttons.length) % buttons.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = buttons.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();

    const nextButton = buttons[nextIndex];

    if (!nextButton) return;

    this.#selectTab(nextButton, { focus: true });
  };

  /**
   * When a tab block is selected in the Shopify editor, switch to it.
   *
   * @param {Event} event
   */
  #handleBlockSelect = (event) => {
    if (!(event.target instanceof Element) || !this.contains(event.target)) return;

    const button =
      event.target instanceof HTMLButtonElement && event.target.matches('[data-tab-trigger]')
        ? event.target
        : event.target.querySelector('[data-tab-trigger]');

    if (!(button instanceof HTMLButtonElement)) return;

    this.#selectTab(button);
    button.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  };

  #syncSelectedTab() {
    const buttons = this.#tabButtons;

    if (!buttons.length) return;

    const selectedButton =
      buttons.find((button) => button.getAttribute('aria-selected') === 'true') ||
      buttons.find((button) => button.hasAttribute('data-initial-active')) ||
      buttons[0];

    if (!selectedButton) return;

    this.#selectTab(selectedButton);
  }

  /**
   * @param {HTMLButtonElement} button
   * @param {{ focus?: boolean }} [options]
   */
  #selectTab(button, options = {}) {
    const targetPanelId = button.getAttribute('aria-controls');

    if (!targetPanelId) return;

    const { focus = false } = options;

    this.#tabButtons.forEach((tabButton) => {
      const selected = tabButton === button;

      tabButton.setAttribute('aria-selected', String(selected));
      tabButton.tabIndex = selected ? 0 : -1;
    });

    this.#tabPanels.forEach((panel) => {
      panel.hidden = panel.id !== targetPanelId;
    });

    if (focus) {
      button.focus();
    }
  }
}

if (!customElements.get('tabs-component')) {
  customElements.define('tabs-component', TabsComponent);
}
